import { getClient } from '@api/client';
import { graphql } from '@api/generated';
import {
  DeviceType,
  UserActionEventAction,
  UserActionEventStatus,
} from '@api/generated/graphql';
import { UserActionDeviceType, UserActionEventType } from '../lib/types';
import type { UserActionEvent } from '../lib/types';

const ADD_INTERACTION_EVENTS = graphql(`
  mutation addInteractionEvents($input: AddInteractionEventsInput!) {
    addInteractionEvents(input: $input) {
      results {
        clientEventId
        status
        error
      }
    }
  }
`);

function toEventAction(type: UserActionEventType): UserActionEventAction {
  switch (type) {
    case UserActionEventType.AgentRejected:
      return UserActionEventAction.AgentRejected;
    case UserActionEventType.AgentHoldStarted:
      return UserActionEventAction.AgentHoldStarted;
    case UserActionEventType.AgentHoldEnded:
      return UserActionEventAction.AgentHoldEnded;
    default: {
      const _exhaustive: never = type;
      throw new Error(`Unhandled UserActionEventType: ${_exhaustive}`);
    }
  }
}

function toDeviceType(type: UserActionDeviceType): DeviceType {
  switch (type) {
    case UserActionDeviceType.Web:
      return DeviceType.Web;
    case UserActionDeviceType.Mobile:
      return DeviceType.Mobile;
    case UserActionDeviceType.DeskPhone:
      return DeviceType.DeskPhone;
    default: {
      const _exhaustive: never = type;
      throw new Error(`Unhandled UserActionDeviceType: ${_exhaustive}`);
    }
  }
}

export interface FlushResult {
  /** IDs to remove from IndexedDB after a successful BFF accept. */
  toDelete: string[];
  /** IDs to keep and flush again: any non-Success response (Retryable, Failed, etc.). */
  toRetry: string[];
}

/**
 * Posts a batch of UserActionEvents to the BFF and partitions the results by
 * what should happen next:
 *
 * - Success   → toDelete (delivered)
 * - Failed    → toRetry (retained; see UserActionEventTable max-retry handling)
 * - Retryable → toRetry (transient; keep in IndexedDB and try again)
 */
export async function addInteractionEvents(
  events: UserActionEvent[],
  options?: { keepalive?: boolean },
): Promise<FlushResult> {
  const result = await getClient().mutate({
    mutation: ADD_INTERACTION_EVENTS,
    variables: {
      input: {
        events: events.map((e) => ({
          clientEventId: e.eventId,
          kazooCallId: e.kazooCallId,
          event: toEventAction(e.event),
          timestamp: e.timestamp.toISOString(),
          deviceType: toDeviceType(e.deviceType),
          userId: e.userId,
        })),
      },
    },
    ...(options?.keepalive && {
      context: { fetchOptions: { keepalive: true } },
    }),
  });

  if (!result.data) {
    throw new Error('addInteractionEvents returned no data');
  }
  const results = result.data.addInteractionEvents.results;
  return {
    toDelete: results
      .filter((r) => r.status === UserActionEventStatus.Success)
      .map((r) => r.clientEventId),
    toRetry: results
      .filter((r) => r.status !== UserActionEventStatus.Success)
      .map((r) => r.clientEventId),
  };
}
